import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Attach JWT if present
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('dz_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export default api;

// ----- API helpers -----
export const AuthAPI = {
  register: (data) => api.post('/auth/register', data).then(r => r.data),
  login: (data) => api.post('/auth/login', data).then(r => r.data),
  googleSession: (session_id, extras = {}) => api.post('/auth/google/session', { session_id, ...extras }).then(r => r.data),
  me: () => api.get('/auth/me').then(r => r.data),
  logout: () => api.post('/auth/logout').then(r => r.data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }).then(r => r.data),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }).then(r => r.data),
};

export const CatalogAPI = {
  categories: () => api.get('/categories').then(r => r.data),
  brands: () => api.get('/brands').then(r => r.data),
  suppliers: () => api.get('/suppliers').then(r => r.data),
  products: (params) => api.get('/products', { params }).then(r => r.data),
  product: (id) => api.get(`/products/${id}`).then(r => r.data),
  offers: (id) => api.get(`/products/${id}/offers`).then(r => r.data),
  reviews: (id) => api.get(`/products/${id}/reviews`).then(r => r.data),
  store: (slug) => api.get(`/store/${slug}`).then(r => r.data),
};

export const BuyerAPI = {
  favorites: () => api.get('/favorites').then(r => r.data),
  addFavorite: (product_id) => api.post('/favorites', { product_id }).then(r => r.data),
  removeFavorite: (id) => api.delete(`/favorites/${id}`).then(r => r.data),
  createOrder: (data) => api.post('/orders', data).then(r => r.data),
  myOrders: () => api.get('/orders/me').then(r => r.data),
  postReview: (data) => api.post('/reviews', data).then(r => r.data),
};

export const SupplierAPI = {
  products: () => api.get('/supplier/products').then(r => r.data),
  createProduct: (data) => api.post('/supplier/products', data).then(r => r.data),
  updateProduct: (id, data) => api.put(`/supplier/products/${id}`, data).then(r => r.data),
  deleteProduct: (id) => api.delete(`/supplier/products/${id}`).then(r => r.data),
  orders: () => api.get('/supplier/orders').then(r => r.data),
  updateOrderStatus: (id, status) => api.put(`/supplier/orders/${id}/status`, { status }).then(r => r.data),
  // Profile / shipping settings
  me: () => api.get('/supplier/me').then(r => r.data),
  updateMe: (data) => api.patch('/supplier/me', data).then(r => r.data),
  setProductImage: (product_id, image) => api.patch('/supplier/product-image', { product_id, image }).then(r => r.data),
  setProductCategory: (product_id, category) => api.patch('/supplier/product-category', { product_id, category }).then(r => r.data),
  // New: multi-supplier comparator features
  addOffer: (data) => api.post('/supplier/offers', data).then(r => r.data),
  updateAvailability: (data) => api.put('/supplier/availability', data).then(r => r.data),
  createBrand: (name) => api.post('/supplier/brands', { name }).then(r => r.data),
  createCategory: (data) => api.post('/supplier/categories', data).then(r => r.data),
  staleOffers: () => api.get('/supplier/stale-offers').then(r => r.data),
  renewAll: (product_ids = null) => api.post('/supplier/renew-availability', { product_ids }).then(r => r.data),
  bulkPriceUpdate: (percent, category = null) => api.post('/supplier/bulk-price-update', { percent, category }).then(r => r.data),
  importCSV: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/supplier/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  uploadImage: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/supplier/upload-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
};

export const MessagingAPI = {
  start: (peer_id, context = 'direct', text = '', image = '') =>
    api.post('/messages/start', { peer_id, context, text, image }).then(r => r.data),
  startSupplier: (supplier_id, context = 'direct', text = '') =>
    api.post('/messages/start-supplier', { supplier_id, context, text }).then(r => r.data),
  send: (conversation_id, text = '', image = '') =>
    api.post('/messages/send', { conversation_id, text, image }).then(r => r.data),
  conversations: () => api.get('/messages/conversations').then(r => r.data),
  conversation: (cid) => api.get(`/messages/conversation/${cid}`).then(r => r.data),
  unreadCount: () => api.get('/messages/unread-count').then(r => r.data),
};

export const BlogAPI = {
  list: () => api.get('/blog').then(r => r.data),
  get: (slug) => api.get(`/blog/${slug}`).then(r => r.data),
  adminList: () => api.get('/admin/blog').then(r => r.data),
  create: (data) => api.post('/admin/blog', data).then(r => r.data),
  update: (id, data) => api.patch(`/admin/blog/${id}`, data).then(r => r.data),
  remove: (id) => api.delete(`/admin/blog/${id}`).then(r => r.data),
};

export const PromoAPI = {
  validate: (code, subtotal) => api.post('/promo/validate', { code, subtotal }).then(r => r.data),
  adminList: () => api.get('/admin/promo-codes').then(r => r.data),
  create: (data) => api.post('/admin/promo-codes', data).then(r => r.data),
  update: (code, data) => api.patch(`/admin/promo-codes/${code}`, data).then(r => r.data),
  remove: (code) => api.delete(`/admin/promo-codes/${code}`).then(r => r.data),
};

export const SiteConfigAPI = {
  public: () => api.get('/site-config').then(r => r.data),
  adminGet: () => api.get('/admin/site-config').then(r => r.data),
  adminSet: (values) => api.put('/admin/site-config', { values }).then(r => r.data),
};

export const AdminAPI = {
  users: () => api.get('/admin/users').then(r => r.data),
  pendingSuppliers: () => api.get('/admin/suppliers/pending').then(r => r.data),
  approveSupplier: (id) => api.post(`/admin/suppliers/${id}/approve`).then(r => r.data),
  rejectSupplier: (id) => api.post(`/admin/suppliers/${id}/reject`).then(r => r.data),
  allSuppliers: () => api.get('/admin/suppliers/all').then(r => r.data),
  stats: () => api.get('/admin/stats').then(r => r.data),

  // Orders
  orders: (params) => api.get('/admin/orders', { params }).then(r => r.data),
  orderStats: () => api.get('/admin/orders/stats').then(r => r.data),
  cancelOrder: (oid, reason) => api.post(`/admin/orders/${oid}/cancel`, { reason }).then(r => r.data),

  // Notifications
  sendNotification: (data) => api.post('/admin/notifications/send', data).then(r => r.data),
  sendNewsletter: (data) => api.post('/admin/notifications/newsletter', data).then(r => r.data),

  // Integrations
  getIntegrations: () => api.get('/admin/integrations').then(r => r.data),
  saveIntegration: (integration, values) => api.put(`/admin/integrations/${integration}`, { values }).then(r => r.data),
  testIntegration: (integration, test_to) => api.post(`/admin/integrations/${integration}/test`, { test_to }).then(r => r.data),

  // System status
  systemStatus: () => api.get('/admin/system/status').then(r => r.data),

  // Email Sequences (drip campaigns)
  listSequences:       () => api.get('/admin/email-sequences').then(r => r.data),
  createSequence:      (data) => api.post('/admin/email-sequences', data).then(r => r.data),
  patchSequence:       (id, data) => api.patch(`/admin/email-sequences/${id}`, data).then(r => r.data),
  deleteSequence:      (id) => api.delete(`/admin/email-sequences/${id}`).then(r => r.data),
  runSequenceNow:      (id) => api.post(`/admin/email-sequences/${id}/run-now`).then(r => r.data),
  sequencesStats:      () => api.get('/admin/email-sequences/stats').then(r => r.data),
  sequencePerformance: (id, days = 7) => api.get(`/admin/email-sequences/${id}/performance`, { params: { attribution_days: days } }).then(r => r.data),

  // Products
  products: (params) => api.get('/admin/products', { params }).then(r => r.data),
  createProduct: (data) => api.post('/admin/products', data).then(r => r.data),
  updateProduct: (id, data) => api.patch(`/admin/products/${id}`, data).then(r => r.data),
  deleteProduct: (id) => api.delete(`/admin/products/${id}`).then(r => r.data),
  moveProduct: (id, category) => api.post(`/admin/products/${id}/move`, { category }).then(r => r.data),

  // Brands
  brands: () => api.get('/admin/brands').then(r => r.data),
  createBrand: (data) => api.post('/admin/brands', data).then(r => r.data),
  updateBrand: (oldName, data) => api.patch(`/admin/brands/${encodeURIComponent(oldName)}`, data).then(r => r.data),
  deleteBrand: (name) => api.delete(`/admin/brands/${encodeURIComponent(name)}`).then(r => r.data),

  // Categories
  categories: () => api.get('/admin/categories').then(r => r.data),
  createCategory: (data) => api.post('/admin/categories', data).then(r => r.data),
  updateCategory: (id, data) => api.patch(`/admin/categories/${id}`, data).then(r => r.data),
  deleteCategory: (id) => api.delete(`/admin/categories/${id}`).then(r => r.data),

  // Upload
  upload: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },

  // Partner API keys
  listPartnerKeys:  () => api.get('/admin/partner-keys').then(r => r.data),
  createPartnerKey: (data) => api.post('/admin/partner-keys', data).then(r => r.data),
  patchPartnerKey:  (id, data) => api.patch(`/admin/partner-keys/${id}`, data).then(r => r.data),
  deletePartnerKey: (id) => api.delete(`/admin/partner-keys/${id}`).then(r => r.data),
};

// Helper: resolve image URL — internal `/api/files/{id}` paths need backend prefix
export const resolveImage = (src) => {
  if (!src) return '';
  if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('data:')) return src;
  if (src.startsWith('/api/')) return `${BACKEND_URL}${src}`;
  return src;
};

export const formatPrice = (price) => new Intl.NumberFormat('fr-DZ').format(Math.round(price)) + ' DA';

export const formatRelative = (iso) => {
  if (!iso) return 'Jamais mis à jour';
  const then = new Date(iso);
  const now = new Date();
  const diffH = (now - then) / 36e5;
  if (diffH < 1) return "Mis à jour à l'instant";
  if (diffH < 24) return `Mis à jour il y a ${Math.round(diffH)} h`;
  const diffD = diffH / 24;
  if (diffD < 30) return `Mis à jour il y a ${Math.round(diffD)} j`;
  return then.toLocaleDateString('fr-DZ');
};

export const freshnessColor = (freshness) => {
  if (freshness === 'fresh') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (freshness === 'ok') return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-rose-50 text-rose-700 border-rose-200';
};
